// Experiment arm resolution for a content-wrapper's candidate tiles.
//
// Pure and isomorphic (mirrors tile-country-visibility.ts): a minimal structural tile interface
// decoupled from the generated GraphQL types, and a total function over the request state. Being the
// single resolution point for SSR, the flight payload and hydration is what keeps them in lockstep —
// the same inputs are available in all three, so the same arm comes out.
//
// It never throws: every degenerate content configuration degrades to the control arm.

import {
  EXPERIMENT_ORIGINAL_VARIANT,
  ExperimentAssignment,
  isOriginalVariant,
  armFromSeed,
  ExperimentRequestState,
  parseExperimentTopic,
} from '@/utils/experiment-helper';

interface TopicReference {
  path: string;
}

export interface ExperimentCandidateTile {
  __typename?: string;
  topics?: TopicReference[] | null;
}

export interface ExperimentDecision<T> {
  // The candidates to render in place of the wrapper.
  chosen: T[];
  // Present only when this render newly assigned the session to an arm and that assignment still
  // needs confirming (cookie + exposure). Absent for bots, for QA overrides and for assignments the
  // session already carries.
  renderedAssignment?: ExperimentAssignment;
}

interface ExperimentArm<T> {
  variant: string;
  tiles: T[];
}

interface GroupedCandidates<T> {
  experimentName?: string;
  arms: ExperimentArm<T>[];
  originalTiles: T[];
}

// Groups candidates into arms ordered [original, ...variants sorted by name] — a stable order is
// what makes `% armCount` reproducible across requests and deploys.
//
// The experiment name is taken from the FIRST candidate that carries an experiment topic; a candidate
// tagged for a *different* experiment falls into the control group rather than being dropped,
// because the content model allows only one experiment per wrapper and a stray tag is an editor
// mistake that should degrade rather than disappear.
function groupCandidates<T extends ExperimentCandidateTile>(candidates: T[]): GroupedCandidates<T> {
  const parsed = candidates.map((tile) => ({
    tile,
    assignment: parseExperimentTopic(tile.topics),
  }));

  const experimentName = parsed.find((entry) => entry.assignment)?.assignment?.name;

  if (!experimentName) {
    return { arms: [], originalTiles: candidates };
  }

  const variantGroups = new Map<string, T[]>();
  const originalTiles: T[] = [];

  parsed.forEach(({ tile, assignment }) => {
    const isControl = assignment?.name !== experimentName || isOriginalVariant(assignment.variant);

    if (isControl) {
      originalTiles.push(tile);

      return;
    }

    const group = variantGroups.get(assignment.variant) ?? [];
    group.push(tile);
    variantGroups.set(assignment.variant, group);
  });

  // Explicit code-unit comparator: variant names are restricted to [a-zA-Z0-9_-], so this is plain
  // ASCII order. Deliberately NOT localeCompare — a locale-sensitive order could differ between the
  // server and a visitor's browser locale, and the arm order feeds `% armCount`.
  const variantArms = Array.from(variantGroups.keys())
    .sort((left, right) => {
      if (left < right) {
        return -1;
      }

      return left > right ? 1 : 0;
    })
    .map((variant) => ({ variant, tiles: variantGroups.get(variant) ?? [] }));

  // The control arm keeps its actual topic name ("original-a") when one is tagged, so the recorded
  // assignment and the Metabase rows match what editors see in Crystallize; untagged-only wrappers
  // fall back to the canonical name.
  const controlArmVariant =
    parsed.find(
      ({ assignment }) =>
        assignment?.name === experimentName && isOriginalVariant(assignment.variant),
    )?.assignment?.variant ?? EXPERIMENT_ORIGINAL_VARIANT;

  // An empty control group means every candidate was tagged as a variant. Keeping an empty arm would
  // show a share of visitors nothing at all, so it is left out and the split runs over what exists.
  const arms: ExperimentArm<T>[] =
    originalTiles.length > 0
      ? [{ variant: controlArmVariant, tiles: originalTiles }, ...variantArms]
      : variantArms;

  return { experimentName, arms, originalTiles };
}

// A control-less wrapper (every candidate tagged as a variant) is an editor mistake: bots and
// fail-safe traffic are then served the alphabetically-first variant instead of an original — the
// exact situation the bot handling exists to prevent. Warned once per experiment per runtime so a
// page full of re-renders does not flood the log.
const warnedControlLessExperiments = new Set<string>();

function warnMissingControlArm(experimentName: string): void {
  if (warnedControlLessExperiments.has(experimentName)) {
    return;
  }

  warnedControlLessExperiments.add(experimentName);
  // eslint-disable-next-line no-console -- isomorphic module: reaches the server log during SSR and
  // the browser console during QA without pulling the server logger into the client bundle.
  console.warn(
    `Experiment "${experimentName}" has no control arm: every candidate is tagged as a variant, ` +
      'so bots and fail-safe traffic are served the first variant instead of an original.',
  );
}

// The control content of a wrapper, with no assignment and no bucketing. Used for wrappers that do
// not opt into the experiment mechanism at all (unknown trigger value).
export function resolveControlArm<T extends ExperimentCandidateTile>(candidates: T[]): T[] {
  if (!candidates || candidates.length === 0) {
    return [];
  }

  const { experimentName, arms, originalTiles } = groupCandidates(candidates);

  if (!experimentName) {
    return candidates;
  }

  return originalTiles.length > 0 ? originalTiles : (arms[0]?.tiles ?? []);
}

export function decideExperimentVariant<T extends ExperimentCandidateTile>(
  candidates: T[],
  state: ExperimentRequestState,
): ExperimentDecision<T> {
  // (6) Nothing to choose between.
  if (!candidates || candidates.length === 0) {
    return { chosen: [] };
  }

  // (1) Group into arms and derive the experiment name FIRST — every later rule is keyed by it.
  const { experimentName, arms, originalTiles } = groupCandidates(candidates);

  // (6) No candidate addresses an experiment: the wrapper is not (yet) configured, render as-is.
  if (!experimentName) {
    return { chosen: candidates };
  }

  // Warned only when an actual split is about to run without a control: a lone variant-only
  // candidate is ordinary WIP content that rule 6 below serves as-is, not a misconfiguration.
  if (originalTiles.length === 0 && arms.length > 1) {
    warnMissingControlArm(experimentName);
  }

  const controlTiles = originalTiles.length > 0 ? originalTiles : (arms[0]?.tiles ?? []);

  // (6) A single arm is not an experiment; serve it without polluting the data with an exposure.
  if (arms.length <= 1) {
    return { chosen: controlTiles };
  }

  // (2) Bots always get the control content and are never assigned.
  if (state.isBot) {
    return { chosen: controlTiles };
  }

  // (3) QA override: `?var=<variant>` forces an arm with NO assignment — no cookie, no exposure, no
  // polluted sample. A non-matching value (e.g. the recommended `?var=1` campaign form) deliberately
  // falls through to rules 4/5 instead of being treated as an override.
  const overriddenVariant = state.override?.toLowerCase();

  if (overriddenVariant) {
    const forcedArm = arms.find((arm) => arm.variant === overriddenVariant);

    if (forcedArm) {
      return { chosen: forcedArm.tiles };
    }
  }

  // (4) Honour an assignment the session already carries; it was confirmed on an earlier page load,
  // so no new assignment is emitted. Looked up BY NAME, so an assignment left over from a different
  // experiment never decides this one.
  const existingAssignment = state.assignments.find((entry) => entry.name === experimentName);

  if (existingAssignment) {
    const assignedArm = arms.find((arm) => arm.variant === existingAssignment.variant);

    if (assignedArm) {
      return { chosen: assignedArm.tiles };
    }
  }

  // (5) Deterministic arm selection from the session-derived seed. The control group is assigned and
  // confirmed just like a variant — without `original` rows the experiment has no baseline to
  // measure against in Metabase.
  const armIndex = armFromSeed(state.bucketSeed, experimentName, arms.length);
  const selectedArm = arms[armIndex] ?? arms[0];

  return {
    chosen: selectedArm.tiles,
    renderedAssignment: { name: experimentName, variant: selectedArm.variant },
  };
}
